const BREWHA_FAQ_PAGE_PATH = 'pages/brewing-equipment-faq';
const BREWHA_MAX_FAQ_RESULTS = 6;
const BREWHA_MAX_PAGE_RESULTS = 6;

let brewhaFaqIndexPromise;

function normalizeBrewhaFaqText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getBrewhaFaqPageUrl() {
  const root = window.Shopify?.routes?.root || '/';
  return `${root}${BREWHA_FAQ_PAGE_PATH}`;
}

function getBrewhaPageSearchUrl(query) {
  const root = window.Shopify?.routes?.root || '/';
  const parameters = new URLSearchParams({ q: query });

  parameters.set('resources[type]', 'page');
  parameters.set('resources[limit]', String(BREWHA_MAX_PAGE_RESULTS + 1));
  parameters.set('resources[options][fields]', 'title,body');

  return `${root}search/suggest.json?${parameters.toString()}`;
}

function stripBrewhaSearchHtml(value) {
  const template = document.createElement('template');
  template.innerHTML = String(value || '');
  return template.content.textContent.replace(/\s+/g, ' ').trim();
}

function getBrewhaFallbackPageQuery(query) {
  const ignoredTerms = new Set([
    'a',
    'an',
    'and',
    'for',
    'how',
    'i',
    'in',
    'is',
    'of',
    'the',
    'to',
    'want'
  ]);
  const relatedTerms = {
    opened: 'start',
    opening: 'start',
    setup: 'start',
    starting: 'start'
  };

  return normalizeBrewhaFaqText(query)
    .split(' ')
    .filter((term) => term && !ignoredTerms.has(term))
    .map((term) => relatedTerms[term] || term)
    .join(' ');
}

async function requestBrewhaPageResults(query) {
  const response = await fetch(getBrewhaPageSearchUrl(query), {
    headers: { Accept: 'application/json' }
  });

  if (!response.ok) {
    throw new Error(`Unable to search BREWHA pages: ${response.status}`);
  }

  const suggestions = await response.json();
  return suggestions?.resources?.results?.pages || [];
}

async function loadBrewhaPageResults(query) {
  try {
    let pages = await requestBrewhaPageResults(query);
    const fallbackQuery = getBrewhaFallbackPageQuery(query);

    if (!pages.length && fallbackQuery && fallbackQuery !== normalizeBrewhaFaqText(query)) {
      pages = await requestBrewhaPageResults(fallbackQuery);
    }

    return pages
      .filter((page) => page.handle !== 'brewing-equipment-faq')
      .slice(0, BREWHA_MAX_PAGE_RESULTS)
      .map((page) => ({
        title: page.title || '',
        description: stripBrewhaSearchHtml(page.body),
        url: page.url || '#'
      }))
      .filter((page) => page.title && page.url !== '#');
  } catch (error) {
    console.error('BREWHA page search:', error);
    return [];
  }
}

function loadBrewhaFaqIndex() {
  if (brewhaFaqIndexPromise) return brewhaFaqIndexPromise;

  brewhaFaqIndexPromise = fetch(getBrewhaFaqPageUrl())
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Unable to load BREWHA FAQ page: ${response.status}`);
      }

      return response.text();
    })
    .then((html) => {
      const faqDocument = new DOMParser().parseFromString(html, 'text/html');

      return Array.from(
        faqDocument.querySelectorAll('.brewha-faq__question[id]')
      )
        .map((questionElement) => {
          const question =
            questionElement.querySelector(
              '.brewha-faq__question-summary span:first-child'
            )?.textContent.trim() || '';

          const answer =
            questionElement.querySelector('.brewha-faq__answer')
              ?.textContent.trim() || '';

          return {
            question,
            answer,
            searchableText: normalizeBrewhaFaqText(`${question} ${answer}`),
            url: `${getBrewhaFaqPageUrl()}#${encodeURIComponent(
              questionElement.id
            )}`
          };
        })
        .filter((faq) => faq.question);
    })
    .catch((error) => {
      console.error('BREWHA FAQ search:', error);
      return [];
    });

  return brewhaFaqIndexPromise;
}

function searchBrewhaFaqIndex(faqs, query) {
  const terms = normalizeBrewhaFaqText(query)
    .split(' ')
    .filter((term) => term.length > 1);

  if (!terms.length) return [];

  return faqs
    .filter((faq) =>
      terms.every((term) => faq.searchableText.includes(term))
    )
    .slice(0, BREWHA_MAX_FAQ_RESULTS);
}

function createBrewhaFaqSection(matches) {
  const section = document.createElement('section');
  section.className = 'brewha-search__group brewha-search__faq';
  section.dataset.brewhaFaqResults = '';
  section.setAttribute('aria-labelledby', 'brewha-faq-search-heading');

  const heading = document.createElement('h2');
  heading.id = 'brewha-faq-search-heading';
  heading.className = 'brewha-search__group-heading';
  heading.textContent = 'FAQ answers';

  const grid = document.createElement('div');
  grid.className = 'brewha-search__grid';

  matches.forEach((faq) => {
    const article = document.createElement('article');

    const link = document.createElement('a');
    link.className = 'brewha-search__card';
    link.href = faq.url;

    const content = document.createElement('div');
    content.className = 'brewha-search__card-content';

    const type = document.createElement('span');
    type.className = 'brewha-search__type';
    type.textContent = 'FAQ';

    const question = document.createElement('h3');
    question.textContent = faq.question;

    const answer = document.createElement('p');
    answer.textContent =
      faq.answer.length > 220
        ? `${faq.answer.slice(0, 220).trim()}…`
        : faq.answer;

    const readMore = document.createElement('span');
    readMore.className = 'brewha-search__read-more';
    readMore.textContent = 'View answer →';

    content.append(type, question, answer, readMore);
    link.appendChild(content);
    article.appendChild(link);
    grid.appendChild(article);
  });

  section.append(heading, grid);
  return section;
}

function createBrewhaPageSection(pages) {
  const section = document.createElement('section');
  section.className = 'brewha-search__group';
  section.dataset.brewhaPageResults = '';
  section.setAttribute('aria-labelledby', 'brewha-page-search-heading');

  const heading = document.createElement('h2');
  heading.id = 'brewha-page-search-heading';
  heading.className = 'brewha-search__group-heading';
  heading.textContent = 'Guides & pages';

  const grid = document.createElement('div');
  grid.className = 'brewha-search__grid';

  pages.forEach((page) => {
    const article = document.createElement('article');

    const link = document.createElement('a');
    link.className = 'brewha-search__card';
    link.href = page.url;

    const content = document.createElement('div');
    content.className = 'brewha-search__card-content';

    const type = document.createElement('span');
    type.className = 'brewha-search__type';
    type.textContent = 'Guide & page';

    const title = document.createElement('h3');
    title.textContent = page.title;

    content.append(type, title);

    if (page.description) {
      const description = document.createElement('p');
      description.textContent =
        page.description.length > 220
          ? `${page.description.slice(0, 220).trim()}…`
          : page.description;
      content.appendChild(description);
    }

    const readMore = document.createElement('span');
    readMore.className = 'brewha-search__read-more';
    readMore.textContent = 'View page →';

    content.appendChild(readMore);
    link.appendChild(content);
    article.appendChild(link);
    grid.appendChild(article);
  });

  section.append(heading, grid);
  return section;
}

async function initializeBrewhaFaqSearchPage() {
  const searchPage = document.querySelector('.template-search');
  if (!searchPage || searchPage.dataset.brewhaFaqSearchReady === 'true') {
    return;
  }

  const query = new URLSearchParams(window.location.search).get('q')?.trim();
  if (!query || query.length < 2) return;

  searchPage.dataset.brewhaFaqSearchReady = 'true';

  const [faqIndex, pageResults] = await Promise.all([
    loadBrewhaFaqIndex(),
    loadBrewhaPageResults(query)
  ]);
  const matches = searchBrewhaFaqIndex(faqIndex, query);

  if (!matches.length && !pageResults.length) return;

  searchPage
    .querySelectorAll('[data-brewha-faq-results]')
    .forEach((element) => element.remove());

  let resources = searchPage.querySelector('[data-brewha-search-resources]');

  if (!resources) {
    resources = document.createElement('div');
    resources.className = 'brewha-search__resources page-width';
    resources.dataset.brewhaSearchResources = '';

    const header = searchPage.querySelector('.template-search__header');
    if (header) {
      header.insertAdjacentElement('afterend', resources);
    } else {
      searchPage.prepend(resources);
    }
  }

  if (pageResults.length) {
    resources
      .querySelectorAll('[data-brewha-page-results]')
      .forEach((element) => element.remove());
    resources.prepend(createBrewhaPageSection(pageResults));
  }

  if (matches.length) {
    resources.prepend(createBrewhaFaqSection(matches));
  }
  searchPage.classList.remove('template-search--empty');

  const noResults = searchPage.querySelector('[data-brewha-native-no-results]');
  if (noResults) noResults.hidden = true;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeBrewhaFaqSearchPage);
} else {
  initializeBrewhaFaqSearchPage();
}

document.addEventListener('shopify:section:load', initializeBrewhaFaqSearchPage);
